import React from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  Heading,
  Icon,
  Image,
  Input,
  Textarea,
  Text,
  VStack,
  HStack,
  Divider,
  useColorModeValue,
  useBreakpointValue,
  FormControl,
  FormLabel,
  FormErrorMessage,
  SimpleGrid,
  Avatar,
  Stack,
  AspectRatio
} from '@chakra-ui/react';
import {
  FaWhatsapp,
  FaPhone,
  FaEnvelope,
  FaFacebook,
  FaInstagram,
  FaTiktok,
  FaPaperPlane,
  FaSignInAlt,
  FaMapMarkerAlt,
  FaClock
} from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';
import { useForm } from 'react-hook-form';
import { EmailIcon } from '@chakra-ui/icons';
import Header from '../layout/Header';
import Footer from '../layout/Footer';



const ContactPage = () => {
  // Colors
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const headingSize = useBreakpointValue({ base: 'xl', md: '2xl' });

  // Form handling
  const { register, handleSubmit, formState: { errors } } = useForm();
  const onSubmit = (data) => {
    console.log(data);
    alert('تم إرسال النموذج بنجاح!');
  };

  return (
    <Box dir="rtl">
      <Header />

      {/* Hero Section */}
      <Box 
        bg={secondaryColor}
        color="white" 
        py={24}
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top="-50%"
          right="-50%"
          w="100%"
          h="200%"
          borderRadius="full"
          bg={`${primaryColor}20`}
          filter="blur(40px)"
        />
        <Container maxW="6xl" textAlign="center" position="relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Heading size={headingSize} mb={6} lineHeight="1.2">
              تواصل مع <Box as="span" color={primaryColor}>فريقنا</Box>
            </Heading>
            <Text fontSize="xl" maxW="2xl" mx="auto" opacity={0.9}>
              نحن متواجدون للإجابة على جميع أسئلتكم وتلقي اقتراحاتكم.
            </Text>
          </motion.div>
        </Container>
      </Box>

      {/* Main Contact Section */}
      <Box py={20} bg={bgColor}>
        <Container maxW="6xl">
          <Grid templateColumns={{ base: '1fr', md: '1.5fr 1fr' }} gap={12}>
            {/* Contact Form */}
            <Box>
              <Box 
                bg={cardBg}
                p={10}
                borderRadius="2xl"
                boxShadow="xl"
                position="relative"
                overflow="hidden"
              >
                <Box
                  position="absolute"
                  top="-100px"
                  right="-100px"
                  w="300px"
                  h="300px"
                  borderRadius="full"
                  bg={`${primaryColor}10`}
                  filter="blur(40px)"
                />
                <Heading size="xl" mb={8} color={secondaryColor} position="relative">
                  أرسل لنا رسالة
                  <Box position="absolute" bottom="-10px" right="0" w="60px" h="4px" bg={primaryColor} />
                </Heading>
                
                <form onSubmit={handleSubmit(onSubmit)}>
                  <VStack spacing={6}>
                    <FormControl isInvalid={errors.name}>
                      <FormLabel>الاسم الكامل</FormLabel>
                      <Input
                        type="text"
                        placeholder="اسمك الكامل"
                        size="lg"
                        {...register('name', { required: 'هذا الحقل إجباري' })}
                        focusBorderColor={primaryColor}
                        bg="white"
                        borderColor="gray.200"
                      />
                      <FormErrorMessage>{errors.name && errors.name.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={errors.email}>
                      <FormLabel>عنوان البريد الإلكتروني</FormLabel>
                      <Input
                        type="email"
                        placeholder="بريدك الإلكتروني"
                        size="lg"
                        {...register('email', { 
                          required: 'هذا الحقل إجباري',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'عنوان بريد إلكتروني غير صالح'
                          }
                        })}
                        focusBorderColor={primaryColor}
                        bg="white"
                        borderColor="gray.200"
                      />
                      <FormErrorMessage>{errors.email && errors.email.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={errors.subject}>
                      <FormLabel>الموضوع</FormLabel>
                      <Input
                        type="text"
                        placeholder="موضوع رسالتك"
                        size="lg"
                        {...register('subject', { required: 'هذا الحقل إجباري' })}
                        focusBorderColor={primaryColor}
                        bg="white"
                        borderColor="gray.200"
                      />
                      <FormErrorMessage>{errors.subject && errors.subject.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={errors.message}>
                      <FormLabel>الرسالة</FormLabel>
                      <Textarea
                        placeholder="صف طلبك..."
                        rows={6}
                        size="lg"
                        {...register('message', { required: 'هذا الحقل إجباري' })}
                        focusBorderColor={primaryColor}
                        bg="white"
                        borderColor="gray.200"
                      />
                      <FormErrorMessage>{errors.message && errors.message.message}</FormErrorMessage>
                    </FormControl>

                    <Button
                      type="submit"
                      colorScheme="yellow"
                      bg={primaryColor}
                      color={secondaryColor}
                      size="lg"
                      leftIcon={<FaPaperPlane />}
                      mt={4}
                      px={10}
                      _hover={{ 
                        transform: 'translateY(-2px)', 
                        boxShadow: 'md',
                        bg: '#E5B817' // Slightly darker yellow
                      }}
                      transition="all 0.2s"
                    >
                      إرسال الرسالة
                    </Button>
                  </VStack>
                </form>
              </Box>
            </Box>

            {/* Contact Information */}
            <Box>
              <Heading size="xl" mb={8} color={secondaryColor} position="relative">
                معلومات الاتصال بنا
                <Box position="absolute" bottom="-10px" right="0" w="60px" h="4px" bg={primaryColor} />
              </Heading>
              
              <VStack spacing={8} align="start">
                {/* Contact Cards */}
                <Box w="full" p={6} bg={cardBg} borderRadius="xl" boxShadow="md">
                  <HStack spacing={4} align="start">
                    <Avatar bg={primaryColor} color={secondaryColor} icon={<FaEnvelope />} />
                    <Box>
                      <Text fontWeight="bold" fontSize="lg" mb={2}>البريد الإلكتروني</Text>
                      <Text color="gray.600">loussifiacademy@gmail.com</Text>
                      <Button
                        as="a"
                        href="mailto:loussifiacademy@gmail.com"
                        mt={2}
                        size="sm"
                        variant="outline"
                        colorScheme="yellow"
                        leftIcon={<FaEnvelope />}
                      >
                        إرسال بريد إلكتروني
                      </Button>
                    </Box>
                  </HStack>
                </Box>

                <Box w="full" p={6} bg={cardBg} borderRadius="xl" boxShadow="md">
                  <HStack spacing={4} align="start">
                    <Avatar bg={primaryColor} color={secondaryColor} icon={<FaPhone />} />
                    <Box>
                      <Text fontWeight="bold" fontSize="lg" mb={2}>الهاتف</Text>
                      <Text color="gray.600">22779137</Text>
                      <Stack direction={{ base: 'column', sm: 'row' }} mt={2} spacing={2}>
                        <Button
                          as="a"
                          href="tel:+21622779137"
                          size="sm"
                          variant="outline"
                          colorScheme="yellow"
                          leftIcon={<FaPhone />}
                        >
                          اتصل
                        </Button>
                        <Button
                          as="a"
                          href="https://wa.me/21622779137"
                          size="sm"
                          variant="outline"
                          colorScheme="whatsapp"
                          leftIcon={<FaWhatsapp />}
                        >
                          واتساب
                        </Button>
                      </Stack>
                    </Box>
                  </HStack>
                </Box>

                <Box w="full" p={6} bg={cardBg} borderRadius="xl" boxShadow="md">
                  <HStack spacing={4} align="start">
                    <Avatar bg={primaryColor} color={secondaryColor} icon={<FaClock />} />
                    <Box>
                      <Text fontWeight="bold" fontSize="lg" mb={2}>ساعات العمل</Text>
                      <Text color="gray.600">الاثنين - الجمعة: 8 صباحًا - 6 مساءً</Text>
                      <Text color="gray.600">السبت: 9 صباحًا - 1 ظهرًا</Text>
                    </Box>
                  </HStack>
                </Box>

                {/* Social Media */}
                <Box pt={4}>
                  <Heading size="md" mb={4} color={secondaryColor}>
                    تابعنا
                  </Heading>
                  
                  <SimpleGrid columns={3} spacing={4}>
                    <Button
                      as="a"
                      href="https://www.facebook.com/profile.php?id=61564271291091"
                      target="_blank"
                      colorScheme="facebook"
                      leftIcon={<FaFacebook />}
                      size="sm"
                    >
                      فيسبوك
                    </Button>
                    <Button
                      as="a"
                      href="https://www.instagram.com/cobra.education/?hl=fr"
                      target="_blank"
                      colorScheme="pink"
                      leftIcon={<FaInstagram />}
                      size="sm"
                    >
                      انستغرام
                    </Button>
                    <Button
                      as="a"
                      href="https://www.tiktok.com/@cobraedu258"
                      target="_blank"
                      color="black"
                      bg="gray.100"
                      _hover={{ bg: 'gray.200' }}
                      leftIcon={<FaTiktok />}
                      size="sm"
                    >
                      تيك توك
                    </Button>
                  </SimpleGrid>
                </Box>
              </VStack>
            </Box>
          </Grid>
        </Container>
      </Box>

    

      <Footer />
    </Box>
  );
};

export default ContactPage;

